/**
 * Thin Three.js layer: shared uniforms, RawShaderMaterial factory, base geometries, lighting constants.
 * Three is used for the context, matrices, render targets and instanced draws only; every shader is ours.
 */
import {
  BufferAttribute, BufferGeometry, CustomBlending, DoubleSide, FrontSide, GLSL3, InstancedBufferGeometry, Matrix4, NoBlending,
  OneFactor, OneMinusSrcAlphaFactor, RawShaderMaterial, Sphere, SrcAlphaFactor, Vector2, Vector3, Vector4, BackSide,
  type Side, type IUniform, type Texture,
} from 'three';
import { LIGHTING } from '@nanome-lite/look';
import { urpLitBrdf, type UrpBrdf } from './lighting';

export interface SharedUniforms {
  uResolution: IUniform<Vector2>;
  uScale: IUniform<number>;
  uMinPixelRadius: IUniform<number>;
  uDepthBand: IUniform<number>;
  uLightDir: IUniform<Vector3>;
  uLightColor: IUniform<Vector3>;
  uAmbient: IUniform<number>;
  /** impostor Blinn-Phong only; the lit mesh shader takes uBrdf / uDielectricSpec per material (brdfUniforms) */
  uSpecExp: IUniform<number>;
  uSpecColor: IUniform<Vector3>;
  /** main-light shadow map (depth texture with compare mode) or null while shadows are off */
  uShadowMap: IUniform<Texture | null>;
  /** view space -> shadow texture coordinates/depth in [0, 1] */
  uShadowMatrix: IUniform<Matrix4>;
  /** x enabled (0/1), y strength, z half texel in uv (PCF tap offset), w unused */
  uShadowParams: IUniform<Vector4>;
  /** distance fade on |p|^2: [scale, offset] */
  uShadowFade: IUniform<Vector2>;
  /** shadow map edge in texels (impostor texel floor in the caster pass) */
  uShadowMapSize: IUniform<number>;
  /** caster bias in light-space metres: [depth, normal] */
  uShadowBias: IUniform<Vector2>;
}

/** sRGB -> linear with the ImpostorCommon.hlsl:131-134 cubic (same curve as the shaders). */
export function srgbToLinearCubic(c: number): number {
  return c * (c * (c * 0.305306011 + 0.682171111) + 0.012522878);
}

/**
 * Unity Euler (x pitch, y yaw, z roll) in degrees -> unit vector pointing TOWARD the light in GL view space.
 * Unity applies Z, then X, then Y (q = Ry * Rx * Rz) to the local +z forward; the light shines along +forward.
 * For (50, -30, 0): Rx(50)*(0,0,1) = (0, -sin50, cos50); Ry(-30) then gives forward =
 * (cos50*sin(-30), -sin50, cos50*cos(-30)) = (-0.3214, -0.7660, 0.5567) in Unity's left-handed frame.
 * The Unity camera looks down +z and the GL camera down -z, so flip z and negate (direction to the light):
 * toLight_GL = (0.3214, 0.7660, 0.5567): upper right, on the viewer's side. Fixed in view space because the trackball
 * rotates the scene (Environment.prefab:252-258; LIGHTING.directionalEulerDeg).
 */
export function unityLightEulerToGlViewDir(xDeg: number, yDeg: number, _zDeg: number): Vector3 {
  const x = (xDeg * Math.PI) / 180, y = (yDeg * Math.PI) / 180;
  // Rx(x) * (0,0,1)
  const fx0 = 0, fy0 = -Math.sin(x), fz0 = Math.cos(x);
  // Ry(y): x' = x cos + z sin, z' = -x sin + z cos
  const fx = fx0 * Math.cos(y) + fz0 * Math.sin(y);
  const fy = fy0;
  const fz = -fx0 * Math.sin(y) + fz0 * Math.cos(y);
  // Unity forward (fx, fy, fz) -> GL (fx, fy, -fz); to-light = -forward
  return new Vector3(-fx, -fy, fz).normalize();
}

export const LIGHT_DIR_VIEW = unityLightEulerToGlViewDir(...LIGHTING.directionalEulerDeg);
/** Main.unity:17-27 flat ambient (0.666) is an inspector colour, i.e. sRGB; the shader works in linear. */
export const AMBIENT_LINEAR = srgbToLinearCubic(LIGHTING.ambientFlat);
/** Highlight depth band: 1.6 model units (ImpostorHighlightPasses.cs:21), scaled to metres per frame. */
export const DEPTH_BAND_MODEL_UNITS = 1.6;

/**
 * URP Lit BRDF inputs for the two mesh materials (RibbonMaterial.mat:118 smoothness 0.15, SurfaceMaterialOpaque.mat:108
 * smoothness 0.3; both metallic 0). The lit mesh shader evaluates URP's own formulas with these, see lighting.ts.
 */
export const RIBBON_BRDF = urpLitBrdf(LIGHTING.ribbonMaterial.smoothness);
export const SURFACE_BRDF = urpLitBrdf(LIGHTING.surfaceMaterial.smoothness);

/** Per-material uniforms of the lit mesh shader (uBrdf, uDielectricSpec in the LIGHTING block). */
export function brdfUniforms(brdf: UrpBrdf): { uBrdf: IUniform<Vector4>; uDielectricSpec: IUniform<number> } {
  return {
    uBrdf: { value: new Vector4(brdf.roughness2, brdf.roughness2MinusOne, brdf.normalizationTerm, brdf.oneMinusReflectivity) },
    uDielectricSpec: { value: brdf.dielectricSpec },
  };
}

export function createSharedUniforms(): SharedUniforms {
  return {
    uResolution: { value: new Vector2(1, 1) },
    uScale: { value: 0.02 },
    uMinPixelRadius: { value: LIGHTING.impostorMinPixelRadius },
    uDepthBand: { value: DEPTH_BAND_MODEL_UNITS * 0.02 },
    uLightDir: { value: LIGHT_DIR_VIEW.clone() },
    uLightColor: { value: new Vector3(1, 1, 1).multiplyScalar(LIGHTING.directionalIntensity) },
    uAmbient: { value: AMBIENT_LINEAR },
    uSpecExp: { value: LIGHTING.impostorSpecularExponent },
    uSpecColor: { value: new Vector3(1, 1, 1).multiplyScalar(LIGHTING.impostorSpecularColor) },
    uShadowMap: { value: null },
    uShadowMatrix: { value: new Matrix4() },
    uShadowParams: { value: new Vector4(0, 1, 0, 0) },
    uShadowFade: { value: new Vector2(0, 0) },
    uShadowMapSize: { value: LIGHTING.shadows.mapSize },
    uShadowBias: { value: new Vector2(0, 0) },
  };
}

export interface MaterialOptions {
  defines?: Record<string, string | number>;
  uniforms?: Record<string, IUniform>;
  side?: Side;
  depthTest?: boolean;
  depthWrite?: boolean;
  blend?: boolean;
  colorWrite?: boolean;
}

/**
 * `blend` (default on) is straight alpha blending: colour SrcAlpha / OneMinusSrcAlpha as in the impostor passes
 * (SphereImpostorWebGL.shader:83, CylinderImpostorWebGL.shader:89: Blend SrcAlpha OneMinusSrcAlpha) and URP's transparent surfaces; alpha One /
 * OneMinusSrcAlpha so the target stays opaque over an opaque backdrop and comes out premultiplied over a transparent
 * clear (snapshot.ts un-premultiplies). It is CustomBlending on purpose: three.js turns NormalBlending into no
 * blending at all unless `transparent` is set (WebGLState.setMaterial), and `transparent` would move the material to
 * three's transparent list and break the renderOrder sequence the passes rely on.
 */
export function makeMaterial(vert: string, frag: string, shared: SharedUniforms | null, opts: MaterialOptions = {}): RawShaderMaterial {
  const blend = opts.blend !== false;
  const m = new RawShaderMaterial({
    glslVersion: GLSL3,
    vertexShader: vert,
    fragmentShader: frag,
    uniforms: { ...(shared ?? {}), ...(opts.uniforms ?? {}) },
    defines: opts.defines ?? {},
    side: opts.side ?? FrontSide,
    depthTest: opts.depthTest ?? true,
    depthWrite: opts.depthWrite ?? true,
    transparent: false,
    blending: blend ? CustomBlending : NoBlending,
    premultipliedAlpha: false,
  });
  if (blend) {
    m.blendSrc = SrcAlphaFactor; m.blendDst = OneMinusSrcAlphaFactor;
    m.blendSrcAlpha = OneFactor; m.blendDstAlpha = OneMinusSrcAlphaFactor;
  }
  if (opts.colorWrite === false) m.colorWrite = false;
  return m;
}

export { DoubleSide, FrontSide, BackSide };

/** Fullscreen triangle in NDC (`position` vec2). */
export function fullscreenGeometry(): BufferGeometry {
  const g = new BufferGeometry();
  g.setAttribute('position', new BufferAttribute(new Float32Array([-1, -1, 3, -1, -1, 3]), 2));
  g.boundingSphere = unbounded();
  return g;
}

/** 2-component position attributes confuse three's bounding sphere; these meshes are never frustum culled anyway. */
function unbounded(): Sphere { return new Sphere(new Vector3(), Infinity); }

/** Sphere proxy quad: corners in [-1, 1]^2, two triangles (ImpostorSphere.hlsl: 4-vertex camera-facing quad). */
export function sphereQuadGeometry(): InstancedBufferGeometry {
  const g = new InstancedBufferGeometry();
  g.setAttribute('position', new BufferAttribute(new Float32Array([-1, -1, 1, -1, 1, 1, -1, 1]), 2));
  g.setIndex(new BufferAttribute(new Uint16Array([0, 1, 2, 0, 2, 3]), 1));
  g.boundingSphere = unbounded();
  return g;
}

/**
 * Cylinder proxy box: x,z in [-1,1], y in [0,1] (ImpostorCylinder.hlsl:28-31), outward CCW winding so back-face
 * culling keeps the near faces.
 */
export function cylinderBoxGeometry(): InstancedBufferGeometry {
  const g = new InstancedBufferGeometry();
  const v: number[] = [];
  for (const y of [0, 1]) for (const z of [-1, 1]) for (const x of [-1, 1]) v.push(x, y, z);
  // vertex index = x + z*2 + y*4 with x,z,y in {0,1}
  const idx = (x: number, z: number, y: number) => x + z * 2 + y * 4;
  const faces: number[] = [];
  const p = (i: number) => [v[i * 3]!, v[i * 3 + 1]!, v[i * 3 + 2]!];
  // Emit each quad with the winding whose normal points away from the box centre (0, 0.5, 0).
  const quad = (a: number, b: number, c: number, d: number) => {
    const A = p(a), B = p(b), C = p(c);
    const e1 = [B[0]! - A[0]!, B[1]! - A[1]!, B[2]! - A[2]!], e2 = [C[0]! - A[0]!, C[1]! - A[1]!, C[2]! - A[2]!];
    const n = [e1[1]! * e2[2]! - e1[2]! * e2[1]!, e1[2]! * e2[0]! - e1[0]! * e2[2]!, e1[0]! * e2[1]! - e1[1]! * e2[0]!];
    const centre = [(A[0]! + C[0]!) / 2, (A[1]! + C[1]!) / 2 - 0.5, (A[2]! + C[2]!) / 2];
    const outward = n[0]! * centre[0]! + n[1]! * centre[1]! + n[2]! * centre[2]! > 0;
    if (outward) faces.push(a, b, c, a, c, d); else faces.push(a, c, b, a, d, c);
  };
  quad(idx(0, 0, 0), idx(0, 1, 0), idx(1, 1, 0), idx(1, 0, 0)); // y = 0
  quad(idx(0, 0, 1), idx(1, 0, 1), idx(1, 1, 1), idx(0, 1, 1)); // y = 1
  quad(idx(0, 0, 0), idx(1, 0, 0), idx(1, 0, 1), idx(0, 0, 1)); // z = -1
  quad(idx(0, 1, 0), idx(0, 1, 1), idx(1, 1, 1), idx(1, 1, 0)); // z = +1
  quad(idx(0, 0, 0), idx(0, 0, 1), idx(0, 1, 1), idx(0, 1, 0)); // x = -1
  quad(idx(1, 0, 0), idx(1, 1, 0), idx(1, 1, 1), idx(1, 0, 1)); // x = +1
  g.setAttribute('position', new BufferAttribute(new Float32Array(v), 3));
  g.setIndex(new BufferAttribute(new Uint16Array(faces), 1));
  g.boundingSphere = unbounded();
  return g;
}

/** Billboard quad for labels. */
export function labelQuadGeometry(): InstancedBufferGeometry {
  return sphereQuadGeometry();
}

export const IDENTITY = new Matrix4();
